import { useEffect, useState } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import type { ContractorJobRunbook, RunbookField } from "@contractor-quoting/shared-types";
import { Badge, Button, Card, SecondaryButton } from "@contractor-quoting/shared-ui";
import {
  automationLabels,
  type RunbookDetail,
  type RunbookSummary,
} from "./quoting-setup-model.js";
import { JobTypeManager } from "./JobTypeManager.js";

interface Props {
  selected: string | null;
  onSelected: (canonicalJobType: string | null) => void;
}

type EditorSection =
  "overview" | "questions" | "measurements" | "pricing" | "review" | "validation";

export function RunbookWorkspace({ selected, onSelected }: Props) {
  const [items, setItems] = useState<RunbookSummary[]>([]);
  const [search, setSearch] = useState("");
  const [trade, setTrade] = useState("all");
  const [status, setStatus] = useState("all");
  const [error, setError] = useState("");
  const load = () =>
    apiFetch<{ items: RunbookSummary[] }>("/v1/contractor/runbooks").then((result) =>
      setItems(result.items),
    );
  useEffect(() => {
    void load().catch((reason: unknown) =>
      setError(reason instanceof Error ? reason.message : "Runbooks could not be loaded."),
    );
  }, []);

  if (selected)
    return (
      <RunbookEditor
        canonicalJobType={selected}
        onBack={() => {
          onSelected(null);
          void load();
        }}
      />
    );
  const trades = [...new Set(items.map((item) => item.trade))].sort();
  const filtered = items.filter((item) => {
    const term = search.trim().toLowerCase();
    return (
      (!term || `${item.label} ${item.canonicalJobType}`.toLowerCase().includes(term)) &&
      (trade === "all" || item.trade === trade) &&
      (status === "all" || item.state === status || item.automationStatus === status)
    );
  });
  return (
    <div>
      <div className="grid gap-3 md:grid-cols-[1fr_12rem_12rem]">
        <input
          aria-label="Search runbooks"
          className="rounded-xl border border-stone-300 bg-white px-4 py-3"
          placeholder="Search runbooks…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select
          aria-label="Filter by trade"
          className="rounded-xl border border-stone-300 bg-white px-3"
          value={trade}
          onChange={(event) => setTrade(event.target.value)}
        >
          <option value="all">All trades</option>
          {trades.map((item) => (
            <option key={item} value={item}>
              {label(item)}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by readiness"
          className="rounded-xl border border-stone-300 bg-white px-3"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="baseline">Baseline</option>
          <option value="automated">Automated pricing</option>
          <option value="manual">Manual pricing</option>
          <option value="needs_pricing">Needs pricing</option>
        </select>
      </div>
      {error ? (
        <p className="mt-4 rounded-xl bg-red-50 p-3 font-semibold text-red-800">{error}</p>
      ) : null}
      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {filtered.map((item) => (
          <Card className="flex items-start justify-between gap-4" key={item.canonicalJobType}>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-stone-500">
                {label(item.trade)}
              </p>
              <h2 className="mt-1 text-xl font-black">{item.label}</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge className={item.active ? "" : "bg-stone-100 text-stone-700"}>
                  {label(item.state)}
                </Badge>
                <Badge
                  className={
                    item.automationStatus === "automated" ? "" : "bg-amber-100 text-amber-900"
                  }
                >
                  {automationLabels[item.automationStatus]}
                </Badge>
                {item.updateAvailable ? (
                  <Badge className="bg-sky-100 text-sky-900">Baseline update available</Badge>
                ) : null}
              </div>
              <p className="mt-3 text-sm text-stone-500">
                Version {item.version} · {item.canonicalJobType}
              </p>
            </div>
            <SecondaryButton onClick={() => onSelected(item.canonicalJobType)}>
              Open runbook
            </SecondaryButton>
          </Card>
        ))}
      </div>
      <div className="mt-8 border-t border-stone-300 pt-2">
        <JobTypeManager />
      </div>
    </div>
  );
}

function RunbookEditor({
  canonicalJobType,
  onBack,
}: {
  canonicalJobType: string;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<RunbookDetail | null>(null);
  const [definition, setDefinition] = useState<ContractorJobRunbook | null>(null);
  const [section, setSection] = useState<EditorSection>("overview");
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [message, setMessage] = useState("");
  const load = async () => {
    const result = await apiFetch<RunbookDetail>(`/v1/contractor/runbooks/${canonicalJobType}`);
    setDetail(result);
    setDefinition(result.definition);
    setDirty(false);
  };
  useEffect(() => {
    void load().catch((reason: unknown) =>
      setMessage(reason instanceof Error ? reason.message : "Runbook could not be loaded."),
    );
  }, [canonicalJobType]);
  useEffect(() => {
    if (!dirty || !detail || !definition || detail.state !== "draft" || !detail.versionId) return;
    const timeout = window.setTimeout(() => {
      setSaveState("saving");
      void apiFetch<{ revision: number; diagnostics: RunbookDetail["diagnostics"] }>(
        `/v1/contractor/runbooks/${canonicalJobType}/drafts/${detail.versionId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ expectedRevision: detail.revision, definition }),
        },
      )
        .then((result) => {
          setDetail((current) =>
            current
              ? { ...current, revision: result.revision, diagnostics: result.diagnostics }
              : current,
          );
          setDirty(false);
          setSaveState("saved");
        })
        .catch((reason: unknown) => {
          setSaveState("error");
          setMessage(reason instanceof Error ? reason.message : "Autosave failed.");
        });
    }, 650);
    return () => window.clearTimeout(timeout);
  }, [canonicalJobType, definition, detail, dirty]);
  const update = (next: ContractorJobRunbook) => {
    setDefinition(next);
    setDirty(true);
    setSaveState("idle");
  };
  if (!detail || !definition)
    return (
      <Card>
        <p>{message || "Loading runbook…"}</p>
      </Card>
    );
  const customize = async (useLatestBaseline = false) => {
    const result = await apiFetch<RunbookDetail>(
      `/v1/contractor/runbooks/${canonicalJobType}/drafts`,
      { method: "POST", body: JSON.stringify({ useLatestBaseline }) },
    );
    setDetail(result);
    setDefinition(result.definition);
    setMessage("Draft ready. Changes now autosave.");
  };
  const publish = async () => {
    if (!detail.versionId || dirty) return;
    await apiFetch(
      `/v1/contractor/runbooks/${canonicalJobType}/drafts/${detail.versionId}/publish`,
      { method: "POST" },
    );
    setMessage("Runbook published. Activate it when you are ready to use it.");
    await load();
  };
  const activate = async () => {
    if (!detail.versionId) return;
    await apiFetch(
      `/v1/contractor/runbooks/${canonicalJobType}/versions/${detail.versionId}/activate`,
      { method: "POST" },
    );
    setMessage("Runbook activated.");
    await load();
  };
  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button className="font-bold text-emerald-800" onClick={onBack}>
            ← All runbooks
          </button>
          <h2 className="mt-2 text-3xl font-black">{detail.label}</h2>
          <p className="mt-1 text-sm text-stone-500">
            Version {detail.version} · {automationLabels[detail.automationStatus]}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-stone-500">
            {saveState === "saving"
              ? "Saving…"
              : saveState === "saved"
                ? "Saved"
                : saveState === "error"
                  ? "Save failed"
                  : ""}
          </span>
          {detail.state !== "draft" ? (
            detail.updateAvailable ? (
              <Button onClick={() => void customize(true)}>Apply baseline update</Button>
            ) : (
              <Button onClick={() => void customize()}>Customize</Button>
            )
          ) : (
            <Button disabled={dirty || saveState === "saving"} onClick={() => void publish()}>
              Publish draft
            </Button>
          )}
          {detail.state !== "draft" && !detail.active ? (
            <SecondaryButton onClick={() => void activate()}>Activate</SecondaryButton>
          ) : null}
        </div>
      </div>
      {message ? (
        <p className="mt-4 rounded-xl bg-emerald-50 p-3 font-semibold text-emerald-900">
          {message}
        </p>
      ) : null}
      <div className="mt-6 grid gap-5 lg:grid-cols-[15rem_1fr]">
        <nav className="flex gap-2 overflow-auto lg:flex-col">
          {(
            [
              "overview",
              "questions",
              "measurements",
              "pricing",
              "review",
              "validation",
            ] as EditorSection[]
          ).map((item) => (
            <button
              className={`rounded-xl px-4 py-3 text-left font-bold ${section === item ? "bg-emerald-800 text-white" : "bg-white text-stone-700"}`}
              key={item}
              onClick={() => setSection(item)}
            >
              {sectionLabel(item)}
            </button>
          ))}
        </nav>
        <Card>{sectionContent(section, definition, detail, update)}</Card>
      </div>
    </div>
  );
}

function sectionContent(
  section: EditorSection,
  definition: ContractorJobRunbook,
  detail: RunbookDetail,
  update: (next: ContractorJobRunbook) => void,
) {
  const editable = detail.state === "draft";
  if (section === "overview")
    return (
      <div>
        <h3 className="text-xl font-black">Overview and customer presentation</h3>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <FieldLabel label="Price presentation">
            <select
              disabled={!editable}
              className="w-full rounded-xl border p-3"
              value={definition.pricePresentation}
              onChange={(event) =>
                update({
                  ...definition,
                  pricePresentation: event.target
                    .value as ContractorJobRunbook["pricePresentation"],
                })
              }
            >
              <option value="single">Single price</option>
              <option value="starting_at">Starting at</option>
              <option value="range">Range</option>
            </select>
          </FieldLabel>
          <FieldLabel label="Customer rounding (minor units)">
            <input
              disabled={!editable}
              className="w-full rounded-xl border p-3"
              type="number"
              min="1"
              value={definition.customerRoundingIncrementMinor}
              onChange={(event) =>
                update({
                  ...definition,
                  customerRoundingIncrementMinor: Number(event.target.value),
                })
              }
            />
          </FieldLabel>
        </div>
        <p className="mt-5 text-sm text-stone-600">
          Published runbooks are immutable. Customize to create an editable draft.
        </p>
      </div>
    );
  if (section === "questions")
    return (
      <div>
        <h3 className="text-xl font-black">Questions and fields</h3>
        <div className="mt-4 space-y-4">
          {definition.fields.map((field, index) => (
            <FieldEditor
              editable={editable}
              field={field}
              key={field.key}
              onChange={(next) =>
                update({
                  ...definition,
                  fields: definition.fields.map((item, itemIndex) =>
                    itemIndex === index ? next : item,
                  ),
                })
              }
            />
          ))}
        </div>
      </div>
    );
  if (section === "measurements")
    return (
      <ReadOnlySection
        title="Measurements and derived values"
        empty="This baseline uses contractor review instead of an automated measurement calculation."
      >
        {definition.derivedMetrics.map((metric) => (
          <Row
            key={metric.key}
            title={metric.key}
            detail={`${metric.handler} · ${metric.unit} · ${metric.dependencies.join(", ")}`}
          />
        ))}
      </ReadOnlySection>
    );
  if (section === "pricing")
    return (
      <div>
        <h3 className="text-xl font-black">Shared-rate mappings and pricing recipe</h3>
        {!definition.pricingRecipe.length ? (
          <p className="mt-4 text-stone-600">
            This baseline is intentionally marked for manual pricing. No monetary values are
            assumed.
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            {definition.pricingRecipe.map((component, index) => (
              <div className="rounded-xl border border-stone-200 p-4" key={component.code}>
                <p className="font-black">{component.customerCategory}</p>
                <p className="mt-1 text-xs font-semibold text-stone-500">{component.code}</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <FieldLabel label="Shared rate code">
                    <input
                      className="w-full rounded-lg border p-2"
                      disabled={!editable}
                      value={component.rateCode}
                      onChange={(event) =>
                        update({
                          ...definition,
                          pricingRecipe: definition.pricingRecipe.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, rateCode: event.target.value } : item,
                          ),
                        })
                      }
                    />
                  </FieldLabel>
                  <FieldLabel label="Quantity source">
                    <input
                      className="w-full rounded-lg border p-2"
                      disabled={!editable}
                      value={component.quantitySource}
                      onChange={(event) =>
                        update({
                          ...definition,
                          pricingRecipe: definition.pricingRecipe.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, quantitySource: event.target.value }
                              : item,
                          ),
                        })
                      }
                    />
                  </FieldLabel>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  if (section === "review")
    return (
      <ReadOnlySection
        title="Defaults, review, and image guidance"
        empty="No manual review fields are configured."
      >
        {definition.fields
          .filter(
            (field) =>
              field.purposes.includes("manual_review") || field.purposes.includes("feasibility"),
          )
          .map((field) => (
            <Row
              key={field.key}
              title={field.label}
              detail={`${label(field.readiness)} · ${field.help}`}
            />
          ))}
      </ReadOnlySection>
    );
  return (
    <div>
      <h3 className="text-xl font-black">Validation and test scenarios</h3>
      <div className="mt-4 space-y-2">
        {detail.diagnostics.length ? (
          detail.diagnostics.map((item, index) => (
            <p
              className={`rounded-xl p-3 text-sm font-semibold ${item.severity === "error" ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-900"}`}
              key={`${item.code}-${index}`}
            >
              {item.message}
            </p>
          ))
        ) : (
          <p className="rounded-xl bg-emerald-50 p-3 text-emerald-900">
            No validation diagnostics.
          </p>
        )}
      </div>
      <h4 className="mt-6 font-black">Test scenarios</h4>
      {definition.testCases.map((test) => (
        <Row
          key={test.name}
          title={test.name}
          detail={
            test.expectedPriceReady
              ? "Expected to be price-ready"
              : "Expected to require review or pricing"
          }
        />
      ))}
    </div>
  );
}

function FieldEditor({
  field,
  editable,
  onChange,
}: {
  field: RunbookField;
  editable: boolean;
  onChange: (field: RunbookField) => void;
}) {
  return (
    <div className="rounded-xl border border-stone-200 p-4">
      <div className="grid gap-3 md:grid-cols-2">
        <FieldLabel label="Label">
          <input
            disabled={!editable}
            className="w-full rounded-lg border p-2"
            value={field.label}
            onChange={(event) => onChange({ ...field, label: event.target.value })}
          />
        </FieldLabel>
        <FieldLabel label="Required for">
          <select
            disabled={!editable}
            className="w-full rounded-lg border p-2"
            value={field.readiness}
            onChange={(event) =>
              onChange({ ...field, readiness: event.target.value as RunbookField["readiness"] })
            }
          >
            <option value="required_for_price">Price</option>
            <option value="required_for_submission">Submission</option>
            <option value="required_for_contractor_approval">Contractor approval</option>
            <option value="optional">Optional</option>
          </select>
        </FieldLabel>
      </div>
      <FieldLabel label="Customer question">
        <input
          disabled={!editable}
          className="mt-3 w-full rounded-lg border p-2"
          value={field.question}
          onChange={(event) => onChange({ ...field, question: event.target.value })}
        />
      </FieldLabel>
      <FieldLabel label="Help">
        <textarea
          disabled={!editable}
          className="mt-3 w-full rounded-lg border p-2"
          rows={2}
          value={field.help}
          onChange={(event) => onChange({ ...field, help: event.target.value })}
        />
      </FieldLabel>
      <p className="mt-2 text-xs font-semibold text-stone-500">
        {field.key} · {field.purposes.map(label).join(", ")}
      </p>
    </div>
  );
}

function FieldLabel({ label: text, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <label className="block text-sm font-bold text-stone-700">
      <span className="mb-1 block">{text}</span>
      {children}
    </label>
  );
}
function ReadOnlySection({
  title,
  empty,
  children,
}: React.PropsWithChildren<{ title: string; empty: string }>) {
  const list = Array.isArray(children) ? children : [children];
  return (
    <div>
      <h3 className="text-xl font-black">{title}</h3>
      <div className="mt-4 divide-y">
        {list.some(Boolean) ? children : <p className="text-stone-600">{empty}</p>}
      </div>
    </div>
  );
}
function Row({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="py-3">
      <p className="font-bold">{title}</p>
      <p className="mt-1 text-sm text-stone-600">{detail}</p>
    </div>
  );
}
function label(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
function sectionLabel(section: EditorSection) {
  return {
    overview: "Overview",
    questions: "Questions & fields",
    measurements: "Measurements",
    pricing: "Shared-rate mappings",
    review: "Review & images",
    validation: "Validation",
  }[section];
}
